// Persists in-progress DomainSetupWizard state so a user who closes the modal
// after the initial /domains/primary/setup POST (but before submitting
// migration details) can resume. Server has no record of the partial state at
// that point — `user.blogId` only gets set on the final migration submit.

const STORAGE_KEY = "showit:domainSetupDraft:v1";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const CHANGE_EVENT = "showit:domainSetupDraftChanged";

// Never persist secrets to localStorage. The user re-enters them on resume,
// and the wizard re-runs WordPress login validation anyway.
const SENSITIVE_FIELDS = ["blog_admin_pass"];

const stripSensitive = (data = {}) => {
  const cleaned = { ...data };
  for (const field of SENSITIVE_FIELDS) {
    if (field in cleaned) cleaned[field] = "";
  }
  return cleaned;
};

const isFresh = (savedAt) =>
  typeof savedAt === "number" && Date.now() - savedAt < MAX_AGE_MS;

const readRaw = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const loadDraft = (userId) => {
  if (!userId) return null;
  const draft = readRaw();
  if (!draft || draft.userId !== userId || !isFresh(draft.savedAt)) return null;
  return draft;
};

export const saveDraft = (
  userId,
  { path, pathIndex, view, domainConfig, blogSetupData, dns }
) => {
  if (!userId) return;
  const payload = {
    userId,
    savedAt: Date.now(),
    path,
    pathIndex,
    view,
    domainConfig,
    blogSetupData: stripSensitive(blogSetupData),
    dns,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    // Quota exceeded or storage disabled — non-fatal; user just loses resume.
  }
};

export const clearDraft = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    // Best-effort.
  }
};

export const subscribeToDraftChanges = (handler) => {
  window.addEventListener(CHANGE_EVENT, handler);
  // Cross-tab updates fire the native `storage` event, not our custom one.
  const onStorage = (e) => {
    if (e.key === STORAGE_KEY) handler();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener("storage", onStorage);
  };
};
